// src/pages/feed/FeedPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fetchFeed } from "../../services/followService";
import { EntryModel } from "../../models";
import { EntryRow } from "../../components/entries/EntryRow";
import { SpinnerLoading, Pagination } from "../../utils";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import "./FeedPage.css";

const FEED_PAGE_SIZE = 20;

/**
 * Activity feed page (/akis).
 * Shows entries written by users the authenticated user follows, newest-first.
 * Requires authentication — redirects to "/" for unauthenticated users.
 */
const FeedPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [entries, setEntries] = useState<EntryModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFeed = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFeed(page - 1, FEED_PAGE_SIZE);
      setEntries(res.content);
      setTotalPages(res.totalPages);
      setTotalItems(res.totalItems);
    } catch (err: any) {
      setError(err.message || t("feed_load_error"));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) return;
    loadFeed(currentPage);
  }, [isAuthenticated, currentPage, loadFeed]);

  // Redirect unauthenticated users to home
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="feed-page">
      <div className="feed-page__header">
        <h2 className="feed-page__title">{t("feed_title")}</h2>
        <span className="feed-page__subtitle">{t("feed_subtitle")}</span>
      </div>

      {loading && <SpinnerLoading />}

      {!loading && error && (
        <div className="feed-page__error">{error}</div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="feed-page__empty">
          <p>{t("feed_empty")}</p>
          <p className="feed-page__empty-hint">{t("feed_empty_hint")}</p>
        </div>
      )}

      {!loading && !error && entries.length > 0 && (
        <>
          <div className="feed-page__list">
            {entries.map((entry) => {
              // Determine entry type (mirrors ProfileEntryAccordion priority order)
              const isPersonEntry = !!entry.personId;
              const isClubEntry   = !isPersonEntry && !!entry.clubId;
              const isTeamEntry   = !isPersonEntry && !isClubEntry && !!entry.teamId;
              const isCustomTopicEntry = !isPersonEntry && !isClubEntry && !isTeamEntry && !!entry.customTopicId;

              // Resolve topic URL and display label based on entry type
              let topicUrl: string;
              let topicLabel: string;

              if (isPersonEntry) {
                topicUrl   = `/kisi/${entry.personSlug ?? entry.personId}`;
                topicLabel = entry.personFullName ?? "—";
              } else if (isClubEntry) {
                topicUrl   = `/kulup/${entry.clubSlug}`;
                topicLabel = entry.clubName ?? "—";
              } else if (isTeamEntry) {
                // teamSlug is the sport+gender segment; clubSlug is the parent club segment
                topicUrl   = `/takim/${entry.clubSlug}/${entry.teamSlug}`;
                topicLabel = entry.teamName ?? "—";
              } else if (isCustomTopicEntry) {
                // Custom topic ("gündem") entry — link to /gundem/{slug}, caption = topic title
                topicUrl   = `/gundem/${entry.customTopicSlug}`;
                topicLabel = entry.customTopicTitle ?? "—";
              } else {
                // Match entry — use homeTeam vs awayTeam
                topicUrl   = generateMatchUrlFromEntry(
                  entry.homeTeam,
                  entry.awayTeam,
                  entry.matchDate,
                  entry.matchId
                );
                topicLabel = `${entry.homeTeam} vs ${entry.awayTeam}`;
              }

              return (
                <div key={entry.id} className="feed-page__entry-wrapper">
                  {/* Topic context link above each entry */}
                  <Link to={topicUrl} className="feed-page__match-label">
                    {topicLabel}
                  </Link>
                  <EntryRow
                    entry={entry}
                    onDelete={() =>
                      setEntries((prev) => prev.filter((e) => e.id !== entry.id))
                    }
                  />
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="feed-page__pagination">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                paginate={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeedPage;